import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSet, hostSet, loadSets } from "../../actions/setsActions";
import { selectIsHost, selectLiveSetName, selectQueueSongs } from "../../selectors";

const songsWord = (n) => `${n} ${n === 1 ? "song" : "songs"}`;

/**
 * Sets. Hosts see the one on screens now and their saved sets, and start a session from
 * any of them; guests see the live set.
 */
const SetsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const code = useSelector((state) => state.session.Code);
  const isHost = useSelector(selectIsHost);
  const hasKey = useSelector((state) => state.host.hasKey);
  const liveSetId = useSelector((state) => state.session.SetId);
  const liveSetName = useSelector(selectLiveSetName);
  const queue = useSelector(selectQueueSongs);
  const { items: sets, isLoaded, loadFailed } = useSelector((state) => state.sets);
  const [busy, setBusy] = useState(false);

  const newSet = async () => {
    const name = window.prompt("Name for the new set", "");
    if (!name || !name.trim()) return;
    const set = await dispatch(createSet({ name: name.trim() }));
    if (set) navigate(`/sets/${set.Id}`);
  };

  const host = async (set) => {
    if (code && !window.confirm(`Start a new session with “${set.Name}”? The current session stays as it is.`)) return;
    setBusy(true);
    const session = await dispatch(hostSet(set.Id));
    setBusy(false);
    if (session) navigate("/lyrics");
  };

  return (
    <div className="page" data-screen-label="Sets">
      {!code && (
        <button type="button" className="back-link" onClick={() => navigate("/")}>
          ← Start
        </button>
      )}
      <div className="page-head">
        <h2 className="page-title">Sets</h2>
      </div>
      {code && (
        <button type="button" className="card" style={{ textAlign: "left", borderColor: "var(--accent)" }} onClick={() => navigate("/queue")}>
          <span className="wrap">
            <span className="dot" aria-hidden="true" />
            <span className="row-title" style={{ fontSize: 19 }}>
              {liveSetName}
            </span>
          </span>
          <span className="row-sub" style={{ whiteSpace: "normal" }}>
            {songsWord(queue.length)} · live now · {code}
          </span>
        </button>
      )}
      {hasKey && (isHost || !code) && (
        <>
          {sets
            .filter((set) => set.Id !== liveSetId)
            .map((set) => (
              <div key={set.Id} className="card" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button type="button" className="row-main" onClick={() => navigate(`/sets/${set.Id}`)}>
                  <span className="wrap">
                    <span className="picker-item" style={{ padding: 0 }}>
                      <span className="swatch" aria-hidden="true" />
                    </span>
                    <span className="row-title" style={{ fontSize: 19 }}>
                      {set.Name}
                    </span>
                  </span>
                  <span className="row-sub">
                    {songsWord(set.Songs.length)} · about {set.Songs.length * 4} min
                  </span>
                </button>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => host(set)} disabled={busy || set.Songs.length === 0}>
                  Host
                </button>
              </div>
            ))}
          {isLoaded && !loadFailed && sets.length === 0 && (
            <div className="empty">No saved sets yet. A set is a list of songs you can host again and again; bookmark songs into one from the Songs page.</div>
          )}
          {loadFailed && (
            <div className="empty">
              Couldn’t load your sets.{" "}
              <button type="button" className="link-btn" onClick={() => dispatch(loadSets())}>
                Try again
              </button>
            </div>
          )}
          <button type="button" className="btn btn-md btn-dashed" onClick={newSet}>
            ＋ New set
          </button>
        </>
      )}
      {!hasKey && code && !isHost && <p className="note">The host runs the set; you can request songs from the Songs page.</p>}
    </div>
  );
};

export default SetsPage;
